import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { TopBar } from '@/components/ui/TopBar';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Save } from 'lucide-react';

interface PromptDTO {
  slug: string;
  bodyMd: string;
  model: string;
  temperature: number;
  maxTokens: number;
  updatedAt: string;
}

export default function AdminPromptsPage() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState('');

  const { data: prompts, isLoading } = useQuery<PromptDTO[]>({
    queryKey: ['admin-prompts'],
    queryFn: () => api.get<PromptDTO[]>('/api/admin/prompts'),
  });

  const save = useMutation({
    mutationFn: (args: { slug: string; bodyMd: string }) =>
      api.put<{ ok: true }>(`/api/admin/prompts/${args.slug}`, { bodyMd: args.bodyMd }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-prompts'] });
      setEditing(null);
    },
  });

  return (
    <div className="safe-bottom min-h-full">
      <TopBar
        title="Промпты"
        subtitle={prompts ? `${prompts.length} штук` : undefined}
      />
      <div className="p-4 space-y-3">
        {isLoading && <div className="text-tg-hint">Загрузка…</div>}

        {prompts?.map((p) => (
          <Card key={p.slug} className="space-y-2">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-mono font-semibold">{p.slug}</div>
                <div className="text-xs text-tg-hint">
                  {p.model} · t={p.temperature} · max={p.maxTokens} · upd{' '}
                  {new Date(p.updatedAt).toLocaleDateString('ru-RU')}
                </div>
              </div>
              {editing === p.slug ? (
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => setEditing(null)}
                    disabled={save.isPending}
                  >
                    Отмена
                  </Button>
                  <Button
                    size="sm"
                    variant="primary"
                    disabled={save.isPending}
                    onClick={() => save.mutate({ slug: p.slug, bodyMd: draft })}
                  >
                    <Save className="w-4 h-4 mr-1" />
                    Сохранить
                  </Button>
                </div>
              ) : (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    setEditing(p.slug);
                    setDraft(p.bodyMd);
                  }}
                >
                  Править
                </Button>
              )}
            </div>

            {editing === p.slug ? (
              <textarea
                className="w-full h-64 p-2 bg-tg-secondaryBg rounded font-mono text-xs outline-none"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
              />
            ) : (
              <details>
                <summary className="text-sm text-tg-hint cursor-pointer">
                  Показать ({p.bodyMd.length.toLocaleString('ru-RU')} символов)
                </summary>
                <pre className="text-xs whitespace-pre-wrap mt-2 bg-tg-secondaryBg/60 p-2 rounded max-h-96 overflow-y-auto">
                  {p.bodyMd}
                </pre>
              </details>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
}
